import { UserRoleTypeEnum } from 'common/constants/user';
import { SnackbarType } from 'common/interfaces';
import { IUser, IUserSearchCriteria } from 'common/interfaces/iuser';
import { StoreUsersActions } from 'reducers/store-users-reducer';
import { UpdateUserActions } from 'reducers/update-user-reducer';
import { UserActions } from 'reducers/user-reducer';
import { getRoleList } from 'services/role-service';
import {
  getUserDetails,
  getUserDetailsByUUId,
  getUserList,
  getUsersForStore,
  postAssignStoresToUser,
  postDeleteUser,
  postRemoveStoresFromUser,
  postUpdateUser,
  searchUser,
} from 'services/user-service';
import { store, useAppDispatch } from 'setup/store';
import { useAppActions } from './app-actions';
import { useTranslation } from 'react-i18next';
import { IStoreUser } from 'common/interfaces/istore';

export const useUserActions = () => {
  const dispatch = useAppDispatch();
  const { showSnackBar } = useAppActions();
  const { t } = useTranslation();

  const setLoading = async (isLoading: boolean) => {
    dispatch(UserActions.setLoading(isLoading));
  };

  const loadAllUsers = async (pageNo: number, pageSize: number) => {
    setLoading(true);
    const resp = await getUserList(pageNo, pageSize);
    console.log('loadAllUsers.....', resp?.data);
    if (resp.status.success) {
      dispatch(UserActions.setUsers(resp.data));
      setLoading(false);
      return resp.data;
    } else {
      showSnackBar(SnackbarType.ERROR, resp?.status?.msg || t('error.loadUsers') || '');
    }
    setLoading(false);
  };

  const loadUsersForStore = async (storeId: number, roleId: number, pageNo: number, pageSize: number) => {
    setLoading(true);
    dispatch(StoreUsersActions.setError(null));
    const users = await getUsersForStore(storeId, roleId, pageNo, pageSize);
    if (users?.status?.success) {
      dispatch(StoreUsersActions.setStoreId(storeId));
      if (pageNo == 1) {
        dispatch(StoreUsersActions.setUsers(users.data));
      } else {
        dispatch(StoreUsersActions.addUsers(users.data));
      }
      setLoading(false);
      return users.data;
    } else {
      showSnackBar(SnackbarType.ERROR, users?.status?.msg || t('error.loadUsers') || '');
    }
    setLoading(false);
  };

  const searchUsers = async (pageNo: number, pageSize: number, criteria: IUserSearchCriteria) => {
    setLoading(true);
    const resp = await searchUser(criteria, pageNo, pageSize);
    console.log('searchUsers.....', resp?.data);
    setLoading(false);
    if (resp.status.success && resp.data?.items) {
      return resp.data;
    } else {
    }
  };

  const getUsersByRole = async (roleIds: number[]) => {
    setLoading(true);
    const resp = await searchUser({ term: '', roleIds }, 1, 100);
    console.log('getUsersByRole.....', resp?.data);
    if (resp.status.success && resp.data?.items) {
      return resp.data;
    } else {
    }
    setLoading(false);
  };

  const loadAllRoles = async () => {
    setLoading(true);
    const resp = await getRoleList();
    console.log('loadAllRoles.....', resp?.data);
    if (resp.status.success && resp.data) {
      dispatch(UserActions.setRoles(resp.data));
      setLoading(false);
      return resp.data;
    } else {
      showSnackBar(SnackbarType.ERROR, resp?.status?.msg || t('error.loadRoles') || '');
    }
    setLoading(false);
  };

  const updateUser = async (user: IUser) => {
    setLoading(true);
    dispatch(UpdateUserActions.setError(null));
    const resp = await postUpdateUser(user);
    console.log('updateUser.....', resp);
    if (resp.status.success && resp.data) {
      dispatch(UpdateUserActions.setUpdated(true));
      dispatch(UpdateUserActions.setUser(resp.data));
      setLoading(false);
      return resp.data;
    } else {
      dispatch(UpdateUserActions.setUpdated(false));
      const msg = resp?.status?.msg || t('error.updateUser', '', user.username) || '';
      dispatch(UpdateUserActions.setError({ code: '', msg }));
    }
    setLoading(false);
  };

  const deleteUser = async (user: IUser) => {
    setLoading(true);
    dispatch(UpdateUserActions.setError(null));
    const resp = await postDeleteUser(user.id);
    console.log('deleteUser.....', resp);
    if (resp.status.success) {
      setLoading(false);
      showSnackBar(SnackbarType.SUCCESS, t('success.deleteUser', '', user.username));
      return resp.status.success;
    } else {
      const msg = resp?.status?.msg || t('error.deleteUser', '', user.username) || '';
      dispatch(UpdateUserActions.setError({ code: '', msg }));
      showSnackBar(SnackbarType.ERROR, resp?.status?.msg || t('error.deleteUser') || '');
    }
    return false;
  };

  const resetUpdateUserForm = () => {
    dispatch(UpdateUserActions.resetContext());
  };
  const setUserTobeUpdated = (user: IUser | null) => {
    dispatch(UpdateUserActions.setUser(user));
  };

  const loadStoreAdmins = async () => {
    let roles = store.getState().userReducer.roles;
    if (!roles || roles.items.length == 0) {
      await loadAllRoles();
      roles = store.getState().userReducer.roles;
    }

    const adminRoleId = roles?.items.find((role) => role.roleName === UserRoleTypeEnum.STORE_ADMIN);
    console.log('adminRoleId::', adminRoleId, roles);

    if (adminRoleId) {
      const userResp = await getUsersByRole([adminRoleId.id]);
      dispatch(UserActions.setStoreAdminUsers(userResp || null));
    }
  };
  const loadFranchiseeAdmins = async () => {
    let roles = store.getState().userReducer.roles;
    if (!roles || roles.items.length == 0) {
      await loadAllRoles();
      roles = store.getState().userReducer.roles;
    }

    const adminRoleId = roles?.items.find((role) => role.roleName === UserRoleTypeEnum.FRANCHISEE_ADMIN);
    console.log('franchiseeAdminRoleId::', adminRoleId, roles);

    if (adminRoleId) {
      const userResp = await getUsersByRole([adminRoleId.id]);
      dispatch(UserActions.setFranchiseeAdminUsers(userResp || null));
    }
  };

  const loadUserDetails = async (userId: number) => {
    const resp = await getUserDetails(userId);
    return resp;
  };

  const loadUserDetailsByUuId = async (uuId: string) => {
    const resp = await getUserDetailsByUUId(uuId);
    return resp;
  };

  const mapStoresToUser = async (userId: number, payload: IStoreUser) => {
    setLoading(true);
    const resp = await postAssignStoresToUser(userId, payload);
    setLoading(false);
    return resp;
  };

  const removeUserStore = async (userId: number, storeId: number) => {
    setLoading(true);
    const resp = await postRemoveStoresFromUser(userId, storeId);
    setLoading(false);
    return resp;
  };

  const setError = (msg: string | undefined, code?: string) => {
    dispatch(UserActions.setError(!msg ? null : { msg, code: code || '' }));
  };

  const setUpdateError = (msg: string | undefined, code?: string) => {
    dispatch(UpdateUserActions.setError(!msg ? null : { msg, code: code || '' }));
  };

  return {
    setLoading,
    loadAllUsers,
    loadAllRoles,
    updateUser,
    resetUpdateUserForm,
    setUserTobeUpdated,
    deleteUser,
    searchUsers,
    getUsersByRole,
    loadStoreAdmins,
    loadUsersForStore,
    loadUserDetails,
    loadFranchiseeAdmins,
    mapStoresToUser,
    removeUserStore,
    setError,
    loadUserDetailsByUuId,
    setUpdateError,
  };
};
